import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';
import { PropertiesPanelSwitch } from './scene-properties-panel/propertiesPanelSwitchProperty';
import { HelpIcon, HelpIconContainer } from '../../../../widgets/helpIconContainer/helpIconContainer';

// LE PAGINE DELLE SETTINGS, UNA LINGUA SOLA (prototipo V3, `.set-page`; utente, 2026-10-19). Ogni pagina ha un
// titolo e una riga che dice di che cosa parla e dove va la modifica; sotto, gruppi con l'etichetta in
// maiuscoletto fuori e il contenuto raccolto in una card, come le sezioni dell'Inspector. Un clic è già la
// scelta: niente Cancel e niente Confirm.

const Page = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px 24px 24px;
`;

const PageHead = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const PageTitle = styled.h3`
    margin: 0;
    color: var(--editor-fg, #262a30);
    font-size: 15px;
    font-weight: 500;
`;

const PageLead = styled.p`
    margin: 0;
    color: var(--editor-muted, #55595f);
    font-size: 12px;
    line-height: 1.4;
`;

export const SettingsPage: FC<{ title: string; lead: string; children: ReactNode }> = ({ title, lead, children }) => (
    <Page>
        <PageHead>
            <PageTitle>{title}</PageTitle>
            <PageLead>{lead}</PageLead>
        </PageHead>
        {children}
    </Page>
);

const Group = styled.section`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const GroupHead = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-height: 22px;
    padding-right: 12px;
`;

const GroupTitle = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
`;

const GroupLabel = styled.span`
    color: var(--editor-faint, #676a6e);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`;

// SPENTA, LA CARD È INERTE (prototipo, `setGroup(..., sw)`): si legge che cosa c'è dentro, ma non si tocca finché
// l'interruttore della testata non la accende.
const GroupCard = styled.div<{ $off?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 10px;
    background: var(--editor-panel, #ffffff);
    opacity: ${(props) => (props.$off ? 0.45 : 1)};
    pointer-events: ${(props) => (props.$off ? 'none' : 'auto')};
`;

// SENZA CARD (prototipo): un contenuto che è già un disegno — le tessere dell'interfaccia — sta sulla pagina.
const GroupBare = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-top: 2px;
`;

// LA «?» DEL BACKOFFICE (utente, 2026-10-20): la spiegazione lunga di una voce, con il suo link alla guida, sta
// nel tooltip della «?» come nei Settings di prima. La riga sotto il nome dice l'essenziale. Grigia, non arancio:
// accanto agli interruttori non deve sembrare un comando acceso.
const HelpTone = styled.span`
    display: inline-flex;

    && * {
        color: var(--editor-faint, #676a6e);
    }
`;

const Help: FC<{ text: ReactNode }> = ({ text }) => (
    <HelpTone>
        <HelpIconContainer tooltip={text} position='top'>
            <HelpIcon />
        </HelpIconContainer>
    </HelpTone>
);

/** Un gruppo della pagina: l'etichetta fuori, a destra l'interruttore che lo accende, e il contenuto in una card. */
export const SettingsGroup: FC<{ title: ReactNode; help?: ReactNode; trailing?: ReactNode; off?: boolean; bare?: boolean; children: ReactNode }> = ({
    title,
    help,
    trailing,
    off,
    bare,
    children
}) => (
    <Group>
        <GroupHead>
            <GroupTitle>
                <GroupLabel>{title}</GroupLabel>
                {help && <Help text={help} />}
            </GroupTitle>
            {trailing}
        </GroupHead>
        {bare ? (
            <GroupBare>{children}</GroupBare>
        ) : (
            <GroupCard $off={off} aria-disabled={off || undefined}>
                {children}
            </GroupCard>
        )}
    </Group>
);

const ToggleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    min-height: 24px;
`;

const ToggleText = styled.span`
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
`;

const ToggleLabel = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--editor-muted, #55595f);
    font-size: 12px;
`;

const ToggleSub = styled.span`
    color: var(--editor-faint, #676a6e);
    font-size: 11px;
`;

/** Una riga con interruttore, come nell'Inspector: nome e spiegazione a sinistra, l'interruttore a destra. */
export const SettingsToggleRow: FC<{
    id: string;
    label: ReactNode;
    sub?: ReactNode;
    help?: ReactNode;
    checked: boolean;
    disabled?: boolean;
    onChange: (checked: boolean) => void;
}> = ({ id, label, sub, help, checked, disabled, onChange }) => (
    <ToggleRow>
        <ToggleText>
            <ToggleLabel>
                <span id={`${id}-label`}>{label}</span>
                {help && <Help text={help} />}
            </ToggleLabel>
            {sub && <ToggleSub>{sub}</ToggleSub>}
        </ToggleText>
        <PropertiesPanelSwitch id={id} checked={checked} labelledBy={`${id}-label`} disabled={disabled} onChange={onChange} />
    </ToggleRow>
);

/** L'interruttore che accende un gruppo intero, nella sua testata. */
export const SettingsGroupSwitch: FC<{ id: string; label: string; checked: boolean; onChange: (checked: boolean) => void }> = ({
    id,
    label,
    checked,
    onChange
}) => <PropertiesPanelSwitch id={id} checked={checked} ariaLabel={label} onChange={onChange} />;

/** Il testo che spiega il gruppo, in cima alla card. */
export const SettingsMessage = styled.p`
    margin: 0;
    color: var(--editor-faint, #676a6e);
    font-size: 11px;
    line-height: 1.4;
`;

/** L'avviso che vale anche a gruppo spento: sta fuori dalla card, sotto. */
export const SettingsNote = styled.p`
    margin: -8px 12px 0;
    color: var(--editor-faint, #676a6e);
    font-size: 11px;
    line-height: 1.4;
`;

/** Un campo dentro una card: il nome a sinistra, il comando a destra. */
export const SettingsFieldRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    min-height: 24px;
`;

export const SettingsFieldLabel = styled.label`
    color: var(--editor-muted, #55595f);
    font-size: 12px;
`;

// Il comando si allarga quanto lo lasciano: senza una misura, in una riga che spinge
// i due capi agli estremi, sarebbe largo quanto gli pare al browser.
export const SettingControl = styled.div`
    width: 180px;
`;
